import { CreateFcrBulkInput, CreateFcrInput, CreateFcrResponse, FcrFcrReporterEnum } from '@/models/gen/graphql';
import { createNotification, handleError } from '@/utils/custom';

import { CreateFcrBulkDocument } from '@/api/queries';
import { GraphApiResponse } from '@/api/core';
import { Toast } from '@/models';
import { Validation } from '@/utils';
import { createActivityForTrip } from './createActivityBulk';
import createGraphApiHook from '@/hooks/createGraphApiHook';
import graphApi from '@/api';

const createFcrValidator = new Validation.Validator({
  description: (val: any): Validation.Validity => ({
    valid: !!(val || '').trim().length && Validation.isAlphanumeric(val) ? Validation.ValidityType.VALID : Validation.ValidityType.INVALID,
    defaultValue: '',
  }),
  cause: (val: any): Validation.Validity => ({
    valid: !!(val || '').trim().length && Validation.isAlphanumeric(val) ? Validation.ValidityType.VALID : Validation.ValidityType.INVALID,
    defaultValue: '',
  }),
  fcrTypeId: (val: string): Validation.Validity => ({
    valid: Validation.isValidUUID(val) ? Validation.ValidityType.VALID : Validation.ValidityType.INVALID,
    defaultValue: '',
  }),
  tripId: (val: string): Validation.Validity => ({
    valid: Validation.isValidUUID(val) ? Validation.ValidityType.VALID : Validation.ValidityType.INVALID,
    defaultValue: '',
  }),
  fcrReporter: (val: FcrFcrReporterEnum): Validation.Validity => ({
    valid:
      val !== null && Object.values(FcrFcrReporterEnum).includes(val) ? Validation.ValidityType.VALID : Validation.ValidityType.INVALID,
    defaultValue: null,
  }),
});

type CreateFcrBulkGraphApiResponse = GraphApiResponse<typeof CreateFcrBulkDocument>;

const title = 'Create Complaint';

const createFcrBulkResponseSelector = (res: CreateFcrBulkGraphApiResponse): CreateFcrResponse[] =>
  !res.errors ? ((res?.createFcrBulk?.output || []) as CreateFcrResponse[]) : undefined;

const [runCreateFcrBulk] = graphApi(CreateFcrBulkDocument, {
  onError: (res: CreateFcrBulkGraphApiResponse): void => handleError(res, { notification: { title } }),
});

const createFcrBulk = async (fcrs: CreateFcrInput[]): Promise<CreateFcrResponse[]> => {
  const values = fcrs.map((fcr: CreateFcrInput): CreateFcrInput => createFcrValidator.create(fcr));
  const input: CreateFcrBulkInput = { values };

  const res = await runCreateFcrBulk({ input });
  const selected = createFcrBulkResponseSelector(res);

  if (selected) createNotification('Success', Toast.Type.SUCCESS, title);

  return selected;
};

export const useCreateFcrBulk = createGraphApiHook(createFcrBulk);

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof CreateFcrInput]?: any;
} & { fcrName: string };
interface ServiceMethod {
  (payload?: ServiceMethodPayload): Promise<CreateFcrResponse>;
}
export const { submitFcr } = ((): {
  submitFcr: ServiceMethod;
} => {
  const submitFcr = async (payload: ServiceMethodPayload) => {
    const { tripId, fcrTypeId, description, cause, fcrName, fcrReporter } = payload || {};
    if (!description || !fcrTypeId || !fcrReporter) return;

    const [res] = await createFcrBulk([
      {
        tripId,
        fcrTypeId,
        description,
        cause,
        fcrReporter,
      },
    ]);

    const fcrActivityPayload: Array<{ tripId: string; comment: string; field: string }> = [
      {
        tripId,
        comment: `${fcrName}|${fcrReporter}|${cause}|${description}`,
        field: 'Fcr',
      },
    ];

    await createActivityForTrip(fcrActivityPayload);
    return res;
  };
  return { submitFcr };
})();

export default createFcrBulk;
